/**
 * tables.js — find the tables in a PDF page and hand back a grid.
 *
 * WHAT MAKES THIS HARD, and what every "PDF to Excel" gets wrong.
 *
 * A PDF has no tables. It has glyphs at coordinates. Whatever looked like a
 * table in Word became, on the way out, a scattering of positioned fragments
 * and — sometimes — a few lines drawn on top. So the job is to recover a
 * structure that was never stored, and the naive recovery fails in a specific,
 * predictable way: cluster the fragments by their LEFT EDGE.
 *
 * Left edges do not line up. Measured on this repository's own contract
 * fixture, a three-column price table:
 *
 *     Позиция@60   Кол-во@143   Цена@198      ← header, left-aligned
 *     Обработка@60      1@155   10 000 ₽@184  ← numbers, centred / right-aligned
 *     Проверка@60       1@152    5 000 ₽@184
 *
 * The middle column starts at 143, 155 and 152; the right one at 198, 184, 184.
 * A left-edge clustering with any tolerance either splits one column into three
 * or merges all three into one. The information that IS stable is the
 * WHITESPACE BETWEEN columns: a vertical corridor no fragment crosses, on every
 * row. That is what this file looks for.
 *
 * TWO SOURCES, AND THE DRAWN ONE WINS WHEN IT EXISTS. A bordered table states
 * its own geometry in vector lines; using them is exact and free. An unbordered
 * one has only the corridors. Most real documents are a mix — a header rule and
 * nothing else — so both run and the drawn lines take precedence where present.
 *
 * WHAT IT DELIBERATELY DOES NOT DO: guess. A region that yields one column is
 * not a table and is returned as nothing, because a spreadsheet full of
 * single-column noise is worse than an honest "no tables found here".
 *
 * Exposed as window.P2XTables.
 */
(function (root) {
    'use strict';
    if (root.P2XTables) return;

    /* A corridor must be wider than a word space to count as a column gap.
       Word spaces run ~0.25–0.5 of the font size; 0.9 leaves room for the
       generously-tracked ones without swallowing a narrow column gap. */
    var GAP_MIN = 0.9;
    /* How many rows may CROSS a corridor and still leave it a column boundary.
       Never zero, and never a fixed share.

       A header routinely spans wider than the column under it — measured on the
       contract fixture, «Кол-во» runs 143…184 while its data cells run 152…161,
       so the corridor before «Цена» is blocked by exactly one row. Demanding a
       corridor clear on EVERY row therefore reports two columns where there are
       three, and does so precisely on the row that names them. A fixed share
       fails the other way: on a twenty-row table it would tolerate four
       crossings and start inventing columns inside prose. */
    function crossingsAllowed(n) { return Math.max(1, Math.round(n * 0.2)); }
    /* Rows closer than this (in line heights) belong to the same table. */
    var ROW_GAP_MAX = 2.4;

    function mean(a) { return a.length ? a.reduce(function (x, y) { return x + y; }, 0) / a.length : 0; }

    // ── rows ─────────────────────────────────────────────────────────────

    /**
     * Fragments → visual rows. Same rule as any text layout: a shared baseline
     * within a fraction of the line height.
     */
    function rows(items) {
        var sorted = items.slice().sort(function (a, b) {
            var d = (a.y + a.h / 2) - (b.y + b.h / 2);
            return Math.abs(d) > Math.max(a.h, b.h) * 0.55 ? d : a.x - b.x;
        });
        var out = [], cur = null;
        sorted.forEach(function (it) {
            var c = it.y + it.h / 2;
            if (cur && Math.abs(c - cur.centre) <= Math.max(it.h, cur.h) * 0.55) {
                cur.items.push(it);
                cur.h = Math.max(cur.h, it.h);
                cur.centre = mean(cur.items.map(function (q) { return q.y + q.h / 2; }));
            } else {
                cur = { items: [it], h: it.h, centre: c };
                out.push(cur);
            }
        });
        out.forEach(function (r) {
            r.items.sort(function (a, b) { return a.x - b.x; });
            r.x0 = Math.min.apply(null, r.items.map(function (q) { return q.x; }));
            r.x1 = Math.max.apply(null, r.items.map(function (q) { return q.x + q.w; }));
            r.y = Math.min.apply(null, r.items.map(function (q) { return q.y; }));
        });
        return out.sort(function (a, b) { return a.centre - b.centre; });
    }

    // ── columns from whitespace ──────────────────────────────────────────

    /**
     * Vertical corridors that most rows leave empty.
     *
     * Implemented as an occupancy histogram over x, one bucket per point, then
     * the runs of zero wider than a word space. Doing it per row and
     * intersecting would be equivalent and far slower; doing it by left edges
     * would be wrong (see the header of this file).
     */
    function corridors(band, lineH) {
        var x0 = Math.floor(Math.min.apply(null, band.map(function (r) { return r.x0; })));
        var x1 = Math.ceil(Math.max.apply(null, band.map(function (r) { return r.x1; })));
        if (!(x1 > x0)) return [];
        var W = x1 - x0;
        var hits = new Uint16Array(W + 1);

        band.forEach(function (r) {
            r.items.forEach(function (it) {
                var a = Math.max(0, Math.floor(it.x - x0));
                var b = Math.min(W, Math.ceil(it.x + it.w - x0));
                for (var i = a; i <= b; i++) hits[i]++;
            });
        });

        var allow = crossingsAllowed(band.length);
        var minGap = Math.max(3, lineH * GAP_MIN);
        var gaps = [], run = -1;
        for (var i = 0; i <= W; i++) {
            var free = hits[i] <= allow;
            if (free && run < 0) run = i;
            if ((!free || i === W) && run >= 0) {
                var end = free ? i : i - 1;
                if (end - run + 1 >= minGap && run > 0 && end < W) {
                    gaps.push({ a: run + x0, b: end + x0 });
                }
                run = -1;
            }
        }
        return gaps;
    }

    /** Corridors → column boundaries (the mid-point of each corridor). */
    function boundsFromGaps(gaps, x0, x1) {
        var edges = [x0];
        gaps.forEach(function (g) { edges.push((g.a + g.b) / 2); });
        edges.push(x1);
        var cols = [];
        for (var i = 0; i < edges.length - 1; i++) cols.push({ x0: edges[i], x1: edges[i + 1] });
        return cols;
    }

    // ── columns from drawn rules ─────────────────────────────────────────

    /**
     * Vertical rules from the page's vector content, as x positions.
     *
     * `lines` come from the caller (it owns pdf.js); each is a device-space
     * segment. Only near-vertical, reasonably tall ones count — a table border
     * is not a tick mark and not an underline.
     */
    function verticalRules(lines, band) {
        var top = Math.min.apply(null, band.map(function (r) { return r.y; }));
        var bot = Math.max.apply(null, band.map(function (r) { return r.y + r.h; }));
        var height = Math.max(1, bot - top);
        var xs = [];
        (lines || []).forEach(function (l) {
            if (Math.abs(l.x1 - l.x0) > 2) return;                 // not vertical
            var a = Math.min(l.y0, l.y1), b = Math.max(l.y0, l.y1);
            if (b - a < height * 0.5) return;                      // too short
            if (b < top - 4 || a > bot + 4) return;                // elsewhere
            xs.push((l.x0 + l.x1) / 2);
        });
        xs.sort(function (p, q) { return p - q; });
        // Merge duplicates: a border drawn as two hairlines is one boundary.
        var merged = [];
        xs.forEach(function (x) {
            if (merged.length && x - merged[merged.length - 1] < 3) return;
            merged.push(x);
        });
        return merged;
    }

    // ── assembling ───────────────────────────────────────────────────────

    /** Consecutive rows with no big vertical break — a candidate table body. */
    function bands(rs) {
        var out = [], cur = null;
        rs.forEach(function (r) {
            if (cur) {
                var prev = cur[cur.length - 1];
                var gap = r.centre - prev.centre;
                if (gap <= Math.max(prev.h, r.h) * ROW_GAP_MAX) { cur.push(r); return; }
            }
            cur = [r];
            out.push(cur);
        });
        return out;
    }

    function cellText(items) {
        var s = '';
        items.forEach(function (it, i) {
            if (i) {
                var prev = items[i - 1];
                var gap = it.x - (prev.x + prev.w);
                if (gap > it.h * 0.18 && !/\s$/.test(s) && !/^\s/.test(it.text)) s += ' ';
            }
            s += it.text;
        });
        return s.trim();
    }

    /**
     * extract(items, lines) → [{ rows: [[cell, …], …], box, cols, source }]
     *
     * `items` are {text, x, y, w, h} in one device space; `lines` are optional
     * drawn segments {x0,y0,x1,y1} in the same space.
     */
    function extract(items, lines, opts) {
        opts = opts || {};
        var minRows = opts.minRows == null ? 2 : opts.minRows;
        var rs = rows(items.filter(function (t) { return t.text && t.text.trim(); }));
        if (!rs.length) return [];

        var tables = [];
        bands(rs).forEach(function (band) {
            if (band.length < minRows) return;
            var lineH = mean(band.map(function (r) { return r.h; })) || 10;
            var x0 = Math.min.apply(null, band.map(function (r) { return r.x0; }));
            var x1 = Math.max.apply(null, band.map(function (r) { return r.x1; }));

            // Drawn rules win where they exist: they are the document stating
            // its own geometry instead of us inferring it.
            var rules = verticalRules(lines, band);
            var cols;
            if (rules.length >= 2) {
                cols = [];
                for (var i = 0; i < rules.length - 1; i++) cols.push({ x0: rules[i], x1: rules[i + 1] });
                var first = cols[0], last = cols[cols.length - 1];
                if (x0 < first.x0 - 2) cols.unshift({ x0: x0, x1: first.x0 });
                if (x1 > last.x1 + 2) cols.push({ x0: last.x1, x1: x1 });
                var srcName = 'rules';
            } else {
                var gaps = corridors(band, lineH);
                if (!gaps.length) return;                 // one column is not a table
                cols = boundsFromGaps(gaps, x0, x1);
                srcName = 'gaps';
            }
            if (cols.length < 2) return;

            /* A fragment that MATERIALLY covers more than one column is a
               merged cell — a heading over two columns, a note running across
               the row. Placing it by its MIDPOINT, as this did until
               2026-08-31, put it in whichever column the middle happened to
               land in: for a heading spanning A and B that is A or B depending
               on the length of the words, which is not a rule anyone can
               predict or correct.

               Excel's own model is the answer: a merged cell's value lives in
               its FIRST cell and the rest are empty. So a spanning fragment
               goes in the leftmost column it covers, and the span is recorded
               so the grid can show it and the workbook can carry a real
               <mergeCell>.

               "Materially" is two thresholds rather than any overlap: a cell
               whose text overhangs its boundary by a few points is not a merge,
               and treating it as one would move ordinary values one column
               left. */
            var spanOf = function (it) {
                var a = it.x, b = it.x + it.w, own = Math.max(1, b - a);
                var hits = [];
                for (var c = 0; c < cols.length; c++) {
                    var ov = Math.min(b, cols[c].x1) - Math.max(a, cols[c].x0);
                    if (ov <= 0) continue;
                    var colW = Math.max(1, cols[c].x1 - cols[c].x0);
                    if (ov >= colW * 0.35 || ov >= own * 0.35) hits.push(c);
                }
                return hits;
            };

            var grid = [], spans = [];
            band.forEach(function (r) {
                var cells = cols.map(function () { return []; });
                var rowSpan = cols.map(function () { return 1; });
                r.items.forEach(function (it) {
                    var hits = spanOf(it);
                    var k;
                    if (hits.length > 1) {
                        k = hits[0];
                        rowSpan[k] = Math.max(rowSpan[k], hits[hits.length - 1] - hits[0] + 1);
                    } else if (hits.length === 1) {
                        k = hits[0];
                    } else {
                        var mid = it.x + it.w / 2;
                        k = mid < cols[0].x0 ? 0 : cols.length - 1;
                        for (var c = 0; c < cols.length; c++) {
                            if (mid >= cols[c].x0 && mid < cols[c].x1) { k = c; break; }
                        }
                    }
                    cells[k].push(it);
                });
                // A span cannot reach past a neighbour that holds its own text:
                // two headings side by side, each a little wide, would otherwise
                // swallow each other.
                for (var c2 = 0; c2 < cols.length; c2++) {
                    while (rowSpan[c2] > 1) {
                        var occupied = false;
                        for (var q = c2 + 1; q < c2 + rowSpan[c2] && q < cols.length; q++) {
                            if (cells[q].length) { occupied = true; break; }
                        }
                        if (!occupied) break;
                        rowSpan[c2]--;
                    }
                    if (c2 + rowSpan[c2] > cols.length) rowSpan[c2] = cols.length - c2;
                }
                grid.push(cells.map(cellText));
                spans.push(rowSpan);
            });

            // A band where nearly every row fills one cell is prose that happened
            // to sit next to a margin note, not a table.
            var filled = grid.map(function (r) {
                return r.filter(function (c) { return c !== ''; }).length;
            });
            if (mean(filled) < 1.6) return;

            tables.push({
                rows: grid,
                spans: spans,
                // The column geometry, kept rather than reduced to a count: a
                // table continuing on the next page is recognised by its
                // BOUNDARIES lining up, and the detector may legitimately find
                // a different number of them on the two pages.
                bounds: cols.map(function (c) { return { x0: c.x0, x1: c.x1 }; }),
                cols: cols.length,
                source: srcName,
                box: {
                    x: x0, y: band[0].y, w: x1 - x0,
                    h: (band[band.length - 1].y + band[band.length - 1].h) - band[0].y,
                },
            });
        });
        return tables;
    }

    root.P2XTables = {
        extract: extract,
        _rows: rows,
        _corridors: corridors,
        _bands: bands,
        _verticalRules: verticalRules,
    };
})(typeof self !== 'undefined' ? self : this);
